import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  UserPlus,
  Settings,
  MessageSquare,
  Trophy,
  Sliders,
  PlusCircle,
  MinusCircle,
  HelpCircle,
  Users,
  AlertTriangle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, GuildConfig, DiscordChannel } from "@/lib/api";

interface InviteTrackerTabProps {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (value: GuildConfig["invite_tracker"]) => void;
}

export function InviteTrackerTab({
  guildId,
  config,
  channels,
  onChange,
}: InviteTrackerTabProps) {
  const rawData = config.invite_tracker || {
    enabled: false,
    log_channel_id: "",
    join_message: "📥 {member} joined, invited by {inviter} (Code: `{code}`, Total: **{invites}**)",
    leave_message: "📤 {member} left, invited by {inviter}",
    fake_threshold_days: 3,
  };

  const [enabled, setEnabled] = useState(rawData.enabled);
  const [logChannelId, setLogChannelId] = useState(rawData.log_channel_id || "");
  const [joinMessage, setJoinMessage] = useState(rawData.join_message || "");
  const [leaveMessage, setLeaveMessage] = useState(rawData.leave_message || "");
  const [fakeThreshold, setFakeThreshold] = useState(rawData.fake_threshold_days || 3);

  // Admin adjustments state
  const [adjustUserId, setAdjustUserId] = useState("");
  const [adjustAmount, setAdjustAmount] = useState(1);
  const [adjustOp, setAdjustOp] = useState<"add" | "remove">("add");

  // Sync state to parent
  const handleSaveLocal = (updates: Partial<typeof rawData>) => {
    onChange({
      enabled,
      log_channel_id: logChannelId,
      join_message: joinMessage,
      leave_message: leaveMessage,
      fake_threshold_days: Number(fakeThreshold),
      ...updates,
    });
  };

  // Fetch leaderboard
  const { data: leaderboardData, isLoading: isLeaderboardLoading, refetch: refetchLeaderboard } = useQuery({
    queryKey: ["invitesLeaderboard", guildId],
    queryFn: () => api.getInvitesLeaderboard(guildId),
    staleTime: 30_000,
  });

  // Admin adjustment mutation
  const adjustMutation = useMutation({
    mutationFn: (body: { user_id: string; amount: number; operation: "add" | "remove" }) =>
      api.adjustInvites(guildId, body),
    onSuccess: (res) => {
      toast.success(
        `Successfully adjusted invites. New bonus for ${res.user_id}: ${res.new_bonus}`
      );
      refetchLeaderboard();
      setAdjustUserId("");
      setAdjustAmount(1);
    },
    onError: (e: any) => {
      toast.error(e.message || "Failed to adjust invites.");
    },
  });

  const handleAdjust = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustUserId.trim()) {
      toast.error("Please enter a Discord User ID.");
      return;
    }
    adjustMutation.mutate({
      user_id: adjustUserId.trim(),
      amount: adjustAmount,
      operation: adjustOp,
    });
  };

  // Pre-configured variable helper badge click handlers
  const insertVariable = (variable: string, target: "join" | "leave") => {
    if (target === "join") {
      setJoinMessage((prev) => prev + variable);
      onChange({
        enabled,
        log_channel_id: logChannelId,
        join_message: joinMessage + variable,
        leave_message: leaveMessage,
        fake_threshold_days: Number(fakeThreshold),
      });
    } else {
      setLeaveMessage((prev) => prev + variable);
      onChange({
        enabled,
        log_channel_id: logChannelId,
        join_message: joinMessage,
        leave_message: leaveMessage + variable,
        fake_threshold_days: Number(fakeThreshold),
      });
    }
    toast.success(`Added ${variable} tag`);
  };

  const textChannels = channels.filter((c) => c.type === 0);

  return (
    <div className="space-y-6">
      {/* Title Header Card */}
      <div className="relative overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-r from-primary/10 to-slate-900/50 p-6 shadow-xl">
        <div className="absolute top-0 right-0 p-4 opacity-5">
          <UserPlus className="h-40 w-40 rotate-12" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground tracking-tight">
              <UserPlus className="h-6 w-6 text-primary" />
              Invite Tracker
              <span className="text-[10px] uppercase bg-primary/20 text-primary font-bold px-2 py-0.5 rounded-full border border-primary/30">
                Beta Test
              </span>
            </h1>
            <p className="text-sm text-muted-foreground max-w-xl">
              Track who invited which member to your server, identify fake/alt accounts, keep join logs clean, and rewards active members.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const next = !enabled;
                setEnabled(next);
                handleSaveLocal({ enabled: next });
                toast.success(next ? "Invite Tracker enabled" : "Invite Tracker disabled");
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                enabled ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-sm font-semibold">
              {enabled ? "Tracking Active" : "Disabled"}
            </span>
          </div>
        </div>
      </div>

      {enabled && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Settings and Config Panel (Left Column) */}
          <div className="xl:col-span-2 space-y-6">
            <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg space-y-6">
              <div className="flex items-center gap-2 border-b border-border/50 pb-3">
                <Settings className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">General Configuration</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold flex items-center gap-1.5 text-muted-foreground">
                    Log Channel
                    <HelpCircle className="h-3.5 w-3.5 opacity-55 tooltip" title="Where welcome and goodbye messages are dispatched" />
                  </label>
                  <select
                    value={logChannelId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLogChannelId(v);
                      handleSaveLocal({ log_channel_id: v });
                    }}
                    className="w-full h-10 px-3 rounded-lg border border-input bg-background/50 hover:bg-background/80 transition text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">-- Select Channel --</option>
                    {textChannels.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        #{ch.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold flex items-center gap-1.5 text-muted-foreground">
                    Fake Account Age Threshold (Days)
                    <HelpCircle className="h-3.5 w-3.5 opacity-55" title="Accounts created less than X days ago will be flagged as Fake" />
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={fakeThreshold}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setFakeThreshold(v);
                      handleSaveLocal({ fake_threshold_days: v });
                    }}
                    className="w-full h-10 px-3 rounded-lg border border-input bg-background/50 hover:bg-background/80 transition text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </Card>

            {/* Custom Welcome & Goodbye Messages */}
            <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg space-y-6">
              <div className="flex items-center gap-2 border-b border-border/50 pb-3">
                <MessageSquare className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">Notification Messages</h2>
              </div>

              {/* Join Notification Template */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-muted-foreground">Join Announcement Message</label>
                  <div className="flex flex-wrap gap-1.5">
                    {["{member}", "{inviter}", "{code}", "{invites}"].map((tag) => (
                      <button
                        key={tag}
                        onClick={() => insertVariable(tag, "join")}
                        className="text-[10px] font-mono bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 rounded border border-primary/20 hover:border-primary/40 transition"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  rows={3}
                  value={joinMessage}
                  onChange={(e) => {
                    const v = e.target.value;
                    setJoinMessage(v);
                    handleSaveLocal({ join_message: v });
                  }}
                  placeholder="📥 {member} joined, invited by {inviter} (Code: {code})"
                  className="w-full p-3 rounded-lg border border-input bg-background/50 hover:bg-background/80 transition text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                />
              </div>

              {/* Leave Notification Template */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-muted-foreground">Leave Announcement Message</label>
                  <div className="flex flex-wrap gap-1.5">
                    {["{member}", "{inviter}"].map((tag) => (
                      <button
                        key={tag}
                        onClick={() => insertVariable(tag, "leave")}
                        className="text-[10px] font-mono bg-primary/10 hover:bg-primary/20 text-primary px-2 py-0.5 rounded border border-primary/20 hover:border-primary/40 transition"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  rows={3}
                  value={leaveMessage}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLeaveMessage(v);
                    handleSaveLocal({ leave_message: v });
                  }}
                  placeholder="📤 {member} left, invited by {inviter}"
                  className="w-full p-3 rounded-lg border border-input bg-background/50 hover:bg-background/80 transition text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                />
              </div>
            </Card>

            {/* Invites Leaderboard */}
            <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg space-y-6">
              <div className="flex items-center justify-between border-b border-border/50 pb-3">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-bold">Top Invite Leaderboard</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => refetchLeaderboard()} className="h-8 text-xs font-semibold">
                  Refresh List
                </Button>
              </div>

              {isLeaderboardLoading ? (
                <div className="h-40 flex items-center justify-center">
                  <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                </div>
              ) : !leaderboardData?.users || leaderboardData.users.length === 0 ? (
                <div className="h-40 flex flex-col items-center justify-center text-center space-y-2 border border-dashed border-border/40 rounded-xl bg-background/25">
                  <Users className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-muted-foreground">No invite data collected yet.</p>
                  <p className="text-xs text-muted-foreground/60 max-w-[280px]">
                    Once members start joining the server with active invite codes, the leaderboard will display here.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border/40">
                  <table className="w-full text-sm border-collapse text-left">
                    <thead className="bg-muted/30 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="p-3">User</th>
                        <th className="p-3 text-center">Total</th>
                        <th className="p-3 text-center">Regular</th>
                        <th className="p-3 text-center">Leaves</th>
                        <th className="p-3 text-center">Fake</th>
                        <th className="p-3 text-center">Bonus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30 bg-background/10">
                      {leaderboardData.users.map((item, idx) => (
                        <tr key={item.id} className="hover:bg-muted/20 transition">
                          <td className="p-3 flex items-center gap-3">
                            <span className="font-semibold text-xs text-primary w-4">#{idx + 1}</span>
                            {item.avatar ? (
                              <img
                                src={`https://cdn.discordapp.com/avatars/${item.id}/${item.avatar}.png`}
                                alt=""
                                className="h-8 w-8 rounded-lg shadow-sm"
                                onError={(e) => {
                                  (e.target as any).src = "https://cdn.discordapp.com/embed/avatars/0.png";
                                }}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                {item.username[0].toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate max-w-[150px]">{item.username}</p>
                              <p className="text-[10px] text-muted-foreground/75 font-mono">{item.id}</p>
                            </div>
                          </td>
                          <td className="p-3 text-center font-bold text-foreground">{item.total}</td>
                          <td className="p-3 text-center text-muted-foreground">{item.regular}</td>
                          <td className="p-3 text-center text-destructive">{item.leave}</td>
                          <td className="p-3 text-center text-amber-500">{item.fake}</td>
                          <td className="p-3 text-center text-primary">+{item.bonus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Admin Management Column (Right Column) */}
          <div className="space-y-6">
            <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg space-y-6">
              <div className="flex items-center gap-2 border-b border-border/50 pb-3">
                <Sliders className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">Admin Adjustments</h2>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-2">
                <h4 className="text-xs font-bold text-primary flex items-center gap-1.5 uppercase tracking-wide">
                  <AlertTriangle className="h-3.5 w-3.5" /> Special Admin Tool
                </h4>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Manually adjust "Bonus" invites for any member of the guild. Changes take effect instantly and update active totals.
                </p>
              </div>

              <form onSubmit={handleAdjust} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">User Discord ID</label>
                  <input
                    type="text"
                    required
                    value={adjustUserId}
                    onChange={(e) => setAdjustUserId(e.target.value)}
                    placeholder="e.g. 1255594847700783155"
                    className="w-full h-10 px-3 rounded-lg border border-input bg-background/50 hover:bg-background/80 transition text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground">Operation</label>
                    <div className="flex rounded-lg border border-input bg-background/50 overflow-hidden h-10">
                      <button
                        type="button"
                        onClick={() => setAdjustOp("add")}
                        className={`flex-1 flex items-center justify-center gap-1 text-xs transition font-semibold ${
                          adjustOp === "add" ? "bg-primary/20 text-primary font-bold border-r border-input/60" : "hover:bg-accent/40 border-r border-input/30"
                        }`}
                      >
                        <PlusCircle className="h-3.5 w-3.5" /> Add
                      </button>
                      <button
                        type="button"
                        onClick={() => setAdjustOp("remove")}
                        className={`flex-1 flex items-center justify-center gap-1 text-xs transition font-semibold ${
                          adjustOp === "remove" ? "bg-destructive/10 text-destructive font-bold" : "hover:bg-accent/40"
                        }`}
                      >
                        <MinusCircle className="h-3.5 w-3.5" /> Sub
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground">Amount</label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="10000"
                      value={adjustAmount}
                      onChange={(e) => setAdjustAmount(Math.max(1, Number(e.target.value)))}
                      className="w-full h-10 px-3 rounded-lg border border-input bg-background/50 hover:bg-background/80 transition text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={adjustMutation.isPending}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-10 flex items-center justify-center gap-2 shadow-lg shadow-primary/15"
                >
                  {adjustMutation.isPending ? "Adjusting..." : "Apply Adjustment"}
                </Button>
              </form>
            </Card>

            <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg space-y-4">
              <h3 className="font-bold text-sm">Tag Placeholders Guide</h3>
              <div className="space-y-3 divide-y divide-border/20 text-xs">
                <div className="pt-0 pb-2 space-y-1">
                  <p className="font-mono text-primary font-bold">{`{member}`}</p>
                  <p className="text-muted-foreground/80">Mentions the newly joined or departed member.</p>
                </div>
                <div className="py-2 space-y-1">
                  <p className="font-mono text-primary font-bold">{`{inviter}`}</p>
                  <p className="text-muted-foreground/80">Mentions the member whose invite link was used.</p>
                </div>
                <div className="py-2 space-y-1">
                  <p className="font-mono text-primary font-bold">{`{code}`}</p>
                  <p className="text-muted-foreground/80">Returns the specific code of the invite used.</p>
                </div>
                <div className="pt-2 space-y-1">
                  <p className="font-mono text-primary font-bold">{`{invites}`}</p>
                  <p className="text-muted-foreground/80">Displays the inviter's total number of active invites.</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
